import React, { useEffect, useMemo, useState } from 'react';

import { AutoComplete, Form, Input, Spin } from 'antd';
import type { FormInstance } from 'antd/es/form';
import { useIntl } from 'react-intl';

import Title from '@src/components/BoxContainer/components/Title';
import { useGeocoding } from '@src/packages/utils';
import {
  mapGeocodeSelectionToUserAddress,
  type GeocodeAddressSelection,
} from '@src/packages/utils/addresses/mapGeocodeSelectionToUserAddress';
import { userHasAttendanceAddress } from '@src/packages/users/utils/userHasAttendanceAddress';
import IntlMessages from '@src/util/IntlMessages';

import styles from './styles.module.less';

type UserAddressValue = {
  country?: string;
  address?: string;
  city?: string;
  region?: string;
  zip?: string;
  location?: { lat: number; lon: number } | null;
};

type GeocodeLocation = {
  label: string;
  lat: number;
  lon: number;
  address?: GeocodeAddressSelection;
};

type UserAddressFieldProps = {
  form: FormInstance;
  disabled?: boolean;
  optionalHintId?: string;
  required?: boolean;
  /** Matches profile-style forms: external labels, no horizontal Form label column */
  labelVariant?: 'default' | 'stacked';
};

const HiddenFieldValue = (_props: { value?: unknown; onChange?: (value: unknown) => void }) =>
  null;

type AddressLineControlProps = {
  value?: unknown;
  onChange?: (value: unknown) => void;
  displayValue: string;
  onDisplayChange: (value: string) => void;
  disabled: boolean;
  options: Array<{ key: string; value: string; label: string; location: GeocodeLocation }>;
  onSearch: (value: string) => void;
  onSelect: (value: string, option: { location: GeocodeLocation }) => void;
  loading: boolean;
  required: boolean;
  placeholder: string;
};

/** Keeps the composed search label while Form.Item owns validation/help for `address.address`. */
const AddressLineControl = ({
  value: _value,
  onChange: _onChange,
  displayValue,
  onDisplayChange,
  disabled,
  options,
  onSearch,
  onSelect,
  loading,
  required,
  placeholder,
}: AddressLineControlProps) => (
  <AutoComplete
    disabled={disabled}
    value={displayValue}
    options={options}
    filterOption={false}
    onSearch={onSearch}
    onSelect={onSelect}
    onChange={onDisplayChange}
    notFoundContent={
      loading ? <Spin size="small" /> : <IntlMessages id="addresses.form.searchNoResults" />
    }
  >
    <Input
      aria-label="address-line"
      aria-required={required ? 'true' : undefined}
      data-testid="user-address-line"
      placeholder={placeholder}
    />
  </AutoComplete>
);

const EMPTY_ADDRESS: UserAddressValue = {
  country: undefined,
  address: undefined,
  city: undefined,
  region: undefined,
  zip: undefined,
  location: undefined,
};

const buildDisplayLine = (address?: UserAddressValue): string => {
  if (!address) {
    return '';
  }
  return [address.address, address.city, address.region, address.zip, address.country]
    .map(part => (typeof part === 'string' ? part.trim() : ''))
    .filter(Boolean)
    .join(', ');
};

/**
 * Single-line address capture backed by free-text geocoding search.
 * Persists the existing work-address summary shape under form path `address`.
 */
const UserAddressField = ({
  form,
  disabled = false,
  optionalHintId,
  required = false,
  labelVariant = 'default',
}: UserAddressFieldProps) => {
  const intl = useIntl();
  const isStacked = labelVariant === 'stacked';
  const { searchLocations, clearLocations, locations, loading } = useGeocoding();
  const watchedAddress = Form.useWatch('address', form) as UserAddressValue | undefined;
  const displayLine = useMemo(() => buildDisplayLine(watchedAddress), [watchedAddress]);
  const [searchText, setSearchText] = useState(displayLine);

  // The stored address is the source of truth: reflect it whenever it changes
  // (form load, selection, clear) without fighting what the user is typing.
  useEffect(() => {
    setSearchText(displayLine);
  }, [displayLine]);

  const options = useMemo(
    () =>
      (locations as GeocodeLocation[]).map((location, index) => ({
        key: `${index}-${location.label}`,
        value: `${index}-${location.label}`,
        label: location.label,
        location,
      })),
    [locations],
  );

  const handleSelect = (_value: string, option: { location: GeocodeLocation }) => {
    const { location } = option;
    const mapped = mapGeocodeSelectionToUserAddress({
      ...location.address,
      latitude: location.lat,
      longitude: location.lon,
      fullAddress: location.label,
    });
    form.setFieldsValue({ address: mapped });
    setSearchText(buildDisplayLine(mapped));
    clearLocations();
  };

  const handleChange = (value: string) => {
    setSearchText(value ?? '');
    if (!value && displayLine) {
      form.setFieldsValue({ address: { ...EMPTY_ADDRESS } });
    }
  };

  const addressRequiredRule = {
    validator: async () => {
      if (userHasAttendanceAddress(form.getFieldValue('address'))) {
        return;
      }
      throw new Error(intl.formatMessage({ id: 'form.required.msg' }));
    },
  };

  const searchField = (
    <Form.Item
      className={isStacked ? styles.addressField : undefined}
      label={isStacked ? null : <IntlMessages id="addresses.form.name" />}
      required={required}
      name={['address', 'address']}
      rules={required ? [addressRequiredRule] : undefined}
    >
      <AddressLineControl
        displayValue={searchText}
        onDisplayChange={handleChange}
        disabled={disabled}
        options={options}
        onSearch={searchLocations}
        onSelect={handleSelect}
        loading={loading}
        required={required}
        placeholder={intl.formatMessage({
          id: required
            ? 'addresses.form.searchPlaceholder.select'
            : 'addresses.form.searchPlaceholder',
        })}
      />
    </Form.Item>
  );

  const fields = (
    <>
      {isStacked ? (
        <div className={styles.stackedLabel}>
          <Title.LabelForm
            required={required}
            value={<IntlMessages id="addresses.form.name" />}
          />
        </div>
      ) : (
        <div className="gx-pb-2 gx-pt-2">
          <Title.SubTitle value={<IntlMessages id="user.form.address.info" />} />
        </div>
      )}
      {!isStacked && optionalHintId && !required ? (
        <Title.Description value={<IntlMessages id={optionalHintId} />} />
      ) : null}
      {required ? (
        <Title.Description value={<IntlMessages id="addresses.form.selectFromList" />} />
      ) : null}
      {isStacked ? <div className={styles.compactInputRow}>{searchField}</div> : searchField}
      <Form.Item name={['address', 'country']} hidden>
        <Input />
      </Form.Item>
      <Form.Item name={['address', 'city']} hidden>
        <Input />
      </Form.Item>
      <Form.Item name={['address', 'region']} hidden>
        <Input />
      </Form.Item>
      <Form.Item name={['address', 'zip']} hidden>
        <Input />
      </Form.Item>
      <Form.Item name={['address', 'location']} hidden>
        <HiddenFieldValue />
      </Form.Item>
    </>
  );

  return isStacked ? <div className={styles.stackedSection}>{fields}</div> : fields;
};

export default UserAddressField;
